import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  MatDrawer,
  MatDrawerContainer,
  MatDrawerContent,
} from '@angular/material/sidenav';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { PermissionService } from '@ipg/ngx-frontend-toolset';
import { UnitService } from '../units/services/unit.service';

@Component({
  selector: 'app-unit-settings',
  imports: [
    MatDrawerContainer,
    MatDrawer,
    MatDrawerContent,
    MatIcon,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsComponent {
  private readonly _unitService = inject(UnitService);
  private readonly _permissionService = inject(PermissionService);

  private readonly _isRootUnit = computed(
    () => this._unitService.currentUnit()?.prevUnitId === null
  );

  private readonly _allMenuItems: {
    label: string;
    icon: string;
    route: string;
    permission?: string | string[];
    rootUnitOnly?: boolean;
  }[] = [
    {
      label: 'Algemeen',
      icon: 'tune',
      route: 'general',
      permission: 'unit:read',
    },
    {
      label: 'Facturatie',
      icon: 'receipt_long',
      route: 'billing',
      permission: 'unit:read',
    },
    {
      label: 'Gebruikers',
      icon: 'group',
      route: 'users',
      permission: 'user:read',
    },
    {
      label: 'Rollen',
      icon: 'shield_person',
      route: 'roles',
      permission: 'user_role:read',
    },
    {
      label: 'Contacten',
      icon: 'account_box',
      route: 'contacts',
      permission: 'contact:read',
    },
    {
      label: 'Variabelen',
      icon: 'data_object',
      route: 'variables',
      permission: 'var:read',
      rootUnitOnly: true,
    },
    {
      label: 'Tags',
      icon: 'label',
      route: 'tags',
      permission: 'tag:read',
      rootUnitOnly: true,
    },
    {
      label: 'Supporttoegang',
      icon: 'support_agent',
      route: 'support-sessions',
      permission: 'support_session:read',
    },
  ];

  protected readonly menuItems = computed(() =>
    this._allMenuItems.filter((item) => {
      if (item.rootUnitOnly && !this._isRootUnit()) return false;
      if (!item.permission) return true;
      return Array.isArray(item.permission)
        ? this._permissionService.hasAnyPermission(item.permission)
        : this._permissionService.hasPermission(item.permission);
    })
  );

  protected readonly aiAgentMenuItems = computed(() => {
    if (!this._isRootUnit()) return [];
    if (!this._permissionService.hasPermission('ai_agent_settings:read')) {
      return [];
    }
    return [
      {
        label: 'Interactie-maker',
        icon: 'smart_toy',
        route: 'ai-agents/creator',
      },
      {
        label: 'Interactie-auditor',
        icon: 'fact_check',
        route: 'ai-agents/auditor',
      },
    ];
  });

  protected readonly logMenuItems = computed(() => {
    if (!this._isRootUnit()) return [];

    const items: {
      label: string;
      icon: string;
      route: string;
    }[] = [];

    if (this._permissionService.hasPermission('audit_log:read')) {
      items.push({
        label: 'Audits',
        icon: 'shield',
        route: 'audit-logs',
      });
    }

    if (this._permissionService.hasPermission('notification_log:read')) {
      items.push({
        label: 'Notificaties',
        icon: 'notifications',
        route: 'notification-logs',
      });
    }

    return items;
  });

  protected readonly actionsMenuItems = computed(() => {
    // Root unit can never be moved — no actions to show.
    if (this._isRootUnit()) return [];

    const items: { label: string; icon: string; route: string }[] = [];

    if (this._permissionService.hasPermission('unit:move')) {
      items.push({
        label: 'Verplaatsen',
        icon: 'swap_horiz',
        route: 'move',
      });
    }

    return items;
  });
}
